import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Skeleton,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CalendarDayIcon from './CalendarDayIcon';
import toast from 'react-hot-toast';
import {
  bookMeeting,
  getFirstAvailableMonth,
  getNewMeetingFirstAvailableMonth,
  getNewMeetingSlots,
  getRescheduleSlots,
  IsraelMeeting,
  rescheduleMeeting,
  Slot,
} from '../../services/israelScheduleService';
import {
  HEBREW_MONTHS,
  HEBREW_WEEKDAYS,
  IL,
  todayYmd,
  ymd,
} from './israelTheme';

export type SchedulingMode = 'book' | 'reschedule';

// Details of the booked slot, passed up for the mobile confirmation popup.
export interface BookedInfo {
  time: string; // "11:00"
  date: string; // "14.08.26"
  day: string; // "14"
}

// A booked meeting's ISO start (Israel time, with offset) -> the popup's parts.
// Read off the string rather than through Date on purpose: the wall-clock the
// clinic booked is what the patient must see, whatever timezone their phone is
// in.
function bookedFromIso(iso: string): BookedInfo | undefined {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return undefined;
  const [, y, mo, d, hh, mm] = m;
  return { time: `${hh}:${mm}`, date: `${d}.${mo}.${y.slice(2)}`, day: d };
}

interface Props {
  token: string;
  meeting: IsraelMeeting;
  mode: SchedulingMode;
  onSuccess: (booked?: BookedInfo) => void;
  // Mobile: render flush inside the strip card (no own background/shadow, and
  // no repeated meeting title — the row above already shows it).
  embedded?: boolean;
}

interface Cursor {
  y: number;
  m: number; // 0-based month
}

// Shared height for the slots + calendar header zones so their divider lines
// land at exactly the same y (each divider is pinned to the bottom of this box).
const HEADER_H = 88;

// Inline calendar + slot picker (Wireframe #2). Reschedule is keyed by
// meeting_id; book-new is keyed by meeting_type_id over the meeting's
// from_date/to_date window.
function SchedulingPanel({
  token,
  meeting,
  mode,
  onSuccess,
  embedded = false,
}: Props) {
  const windowFrom = meeting.from_date; // "YYYY-MM-DD" | null
  const windowTo = meeting.to_date;
  const today = todayYmd();

  // Earliest date we may show: not before today, not before the window start.
  const startBound = useMemo(() => {
    return windowFrom && windowFrom > today ? windowFrom : today;
  }, [windowFrom, today]);

  const [cursor, setCursor] = useState<Cursor | null>(null);
  // Slots already returned by first-available-month for the initial month —
  // consumed once by the month-load effect so it doesn't refetch them.
  const preloadedRef = useRef<{ y: number; m: number; slots: Slot[] } | null>(
    null
  );
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  // Availability could not be read (Yoman timed out, or the request
  // failed). Distinct from "no slots" on purpose - see SlotsResult.
  const [slotsUnavailable, setSlotsUnavailable] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  // Bumped to re-fetch the slots of the CURRENT month without moving the
  // calendar - used when Yoman refuses the slot the patient picked, so the
  // list they choose again from is the live one.
  const [reloadKey, setReloadKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Waiting-list opt-in ("contact me if an earlier slot frees up") — sent on
  // book/reschedule and kept on the meeting's waiting_list field.
  const [waitingList, setWaitingList] = useState(false);
  // Mobile (embedded) is a 2-step wizard: pick a date, then a time.
  const [step, setStep] = useState<'date' | 'time'>('date');

  // Determine the initial month once: jump to the first month that actually
  // has availability (reschedule is keyed by meeting_id, book-new by
  // meeting_type_id within the item's booking window).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fam =
          mode === 'reschedule' && meeting.id != null
            ? await getFirstAvailableMonth(
                token,
                meeting.id,
                windowFrom,
                windowTo
              )
            : await getNewMeetingFirstAvailableMonth(
                token,
                meeting.meetingtype_id,
                windowFrom,
                windowTo
              );
        if (!cancelled && fam.month) {
          const [y, m] = fam.month.split('-').map(Number);
          // Reuse the slots this response already carries for that month —
          // one Yoman call for the whole panel open.
          preloadedRef.current = { y, m: m - 1, slots: fam.slots ?? [] };
          setCursor({ y, m: m - 1 });
          return;
        }
      } catch {
        /* fall through to bound-based month */
      }
      if (cancelled) return;
      const [y, m] = startBound.split('-').map(Number);
      setCursor({ y, m: m - 1 });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load slots for the visible month (clamped to today + the booking window).
  useEffect(() => {
    if (!cursor) return;
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedValue(null);
    (async () => {
      const monthStart = new Date(cursor.y, cursor.m, 1);
      const monthEnd = new Date(cursor.y, cursor.m + 1, 0);
      let from = ymd(monthStart);
      let to = ymd(monthEnd);
      if (from < today) from = today;
      if (windowFrom && windowFrom > from) from = windowFrom;
      if (windowTo && windowTo < to) to = windowTo;

      // First-available-month already returned this month's slots — use them
      // instead of asking Yoman again. Consumed once: navigating away and
      // back refetches fresh slots.
      const preloaded = preloadedRef.current;
      preloadedRef.current = null;
      let list: Slot[] = [];
      let unavailable = false;
      if (preloaded && preloaded.y === cursor.y && preloaded.m === cursor.m) {
        list = preloaded.slots;
      } else if (from <= to) {
        try {
          const res =
            mode === 'book'
              ? await getNewMeetingSlots(
                  token,
                  meeting.meetingtype_id,
                  from,
                  to,
                  meeting.user_id
                )
              : await getRescheduleSlots(
                  token,
                  meeting.id as number,
                  from,
                  to
                );
          list = res.slots;
          unavailable = res.unavailable;
        } catch {
          // A failed request tells us nothing about availability either.
          list = [];
          unavailable = true;
        }
      }
      if (cancelled) return;
      list.sort((a, b) => (a.value < b.value ? -1 : 1));
      setSlots(list);
      setSlotsUnavailable(unavailable);
      // Embedded (mobile) starts on the calendar — slots appear only once a
      // date is picked. Desktop pre-selects the first available date.
      setSelectedDate(list.length && !embedded ? list[0].date : null);
      setLoadingSlots(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, reloadKey]);

  const availableDates = useMemo(
    () => new Set(slots.map((s) => s.date)),
    [slots]
  );
  const daySlots = useMemo(
    () => slots.filter((s) => s.date === selectedDate),
    [slots, selectedDate]
  );

  // "17 ביולי, 2026" for the selected-date chip.
  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) return '';
    const [y, m, d] = selectedDate.split('-').map(Number);
    return `${d} ${HEBREW_MONTHS[m - 1]}, ${y}`;
  }, [selectedDate]);

  const goMonth = (delta: number) => {
    setCursor((c) => {
      if (!c) return c;
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const submit = async () => {
    if (!selectedValue) return;
    setSubmitting(true);
    const slot = slots.find((s) => s.value === selectedValue);
    try {
      // The backend answers {ok:false} on a handled failure (e.g. the Yoman
      // booking was rejected) - that's an error, not a success.
      const res =
        mode === 'book'
          ? await bookMeeting(
              token,
              meeting.meetingtype_id,
              selectedValue,
              meeting.user_id,
              // Pool slots carry the therapist that owns them - required so
              // the Yoman event gets a concrete resource.
              slot?.resource_id,
              waitingList
            )
          : await rescheduleMeeting(
              token,
              meeting.id as number,
              selectedValue,
              waitingList
            );
      if (!res?.ok) {
        if (res?.reason === 'slot_taken') {
          // Yoman would not give the slot the patient picked (it answers by
          // placing the event elsewhere; the server verifies and refuses).
          // Show the live list again rather than a generic failure.
          toast.error('המועד שנבחר כבר אינו זמין, אנא בחרו מועד אחר');
          setSelectedValue(null);
          setReloadKey((k) => k + 1);
          return;
        }
        toast.error('אירעה שגיאה, נסו שוב');
        return;
      }
      // Details of the just-booked meeting for the mobile confirmation popup.
      // The server's `start` is the VERIFIED time of the Yoman event (book and
      // reschedule both read the event back), so it wins over the slot the
      // patient clicked - showing the click back to them is how a patient came
      // to write down a day nobody booked. The slot stays as a fallback for an
      // older backend that does not send `start` yet.
      let booked: BookedInfo | undefined;
      if (res.start) {
        booked = bookedFromIso(res.start);
      } else if (slot) {
        const [y, m, d] = slot.date.split('-');
        booked = { time: slot.display, date: `${d}.${m}.${y.slice(2)}`, day: d };
      }
      onSuccess(booked);
    } catch {
      toast.error('אירעה שגיאה, נסו שוב');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Calendar cell grid -------------------------------------------------
  // Build the month as Sunday-first cells, then reverse each 7-day week so the
  // grid reads right→left (Sunday rightmost, Saturday leftmost) without relying
  // on CSS direction. Each week is padded to 7 before reversing so a short last
  // week stays anchored to the correct (right) side.
  const calendarCells = useMemo(() => {
    if (!cursor) return [] as (number | null)[];
    const firstDow = new Date(cursor.y, cursor.m, 1).getDay(); // 0 Sun..6 Sat
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const leading = Array.from({ length: firstDow }, () => null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const cells: (number | null)[] = [...leading, ...days];
    while (cells.length % 7 !== 0) cells.push(null);

    const ordered: (number | null)[] = [];
    for (let i = 0; i < cells.length; i += 7) {
      ordered.push(...cells.slice(i, i + 7).reverse());
    }
    return ordered;
  }, [cursor]);

  const actionBtnSx = {
    px: 6,
    py: 1.25,
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 700,
    color: IL.white,
    bgcolor: '#17C68C',
    '&:hover': { bgcolor: '#14B37E' },
    '&.Mui-disabled': { bgcolor: '#A5AFBF', color: IL.white, fontWeight: 400 },
  } as const;

  return (
    <>
    <Box
      sx={{
        bgcolor: embedded ? 'transparent' : IL.white,
        borderRadius: embedded ? 0 : 3,
        p: embedded ? 0 : { xs: 2, md: 3 },
        boxShadow: embedded ? 'none' : '0 1px 8px rgba(0,0,0,0.06)',
      }}
    >
      {/* Header: meeting title + duration (title hidden when embedded) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: embedded ? 'flex-start' : 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
        }}
      >
        {!embedded && (
          <>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18 }}>
                פגישת {meeting.therapist_type} - {meeting.meeting_type}
              </Typography>
              {/* Role hint (e.g. "בסמיכות לפגישת הפסיכאטר") under the title */}
              {meeting.hint && (
                <Typography
                  sx={{ fontSize: 13, color: IL.muted, lineHeight: 1.3 }}
                >
                  {meeting.hint}
                </Typography>
              )}
            </Box>
            <Box
              sx={{ width: '2px', height: 22, bgcolor: IL.blueGrey, borderRadius: 1 }}
            />
          </>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: embedded ? 13 : 18 }}>
            זמן: כ-{meeting.length} דק׳
          </Typography>
        </Box>
        {/* Embedded hides the title (the strip row above shows it) - the hint
            wraps onto its own line under the duration. */}
        {embedded && meeting.hint && (
          <Typography
            sx={{ width: '100%', fontSize: 12, color: IL.muted, lineHeight: 1.3 }}
          >
            {meeting.hint}
          </Typography>
        )}
      </Box>

      {/* Mobile time step: selected-date chip (tap to go back and change it). */}
      {embedded && step === 'time' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2,
          }}
        >
          <CalendarDayIcon size={34} check numberColor="#17C68C" strokeColor="#1F2937" />
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setStep('date')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setStep('date');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#E4E7EC',
              borderRadius: '20px',
              px: 2,
              py: 0.75,
              cursor: 'pointer',
            }}
          >
            <Typography sx={{ fontSize: 14, color: '#1F2937' }}>
              {selectedDateLabel}
            </Typography>
            <EditOutlinedIcon sx={{ fontSize: 16, color: '#1F2937' }} />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          // Mobile stacks with the calendar on top; desktop keeps calendar on the right.
          flexDirection: { xs: 'column-reverse', md: 'row-reverse' },
          gap: { xs: 3, md: 8 },
          alignItems: 'flex-start',
        }}
      >
        {/* Slots column — on mobile, shown only in the time step */}
        {(!embedded || step === 'time') && (
        <Box sx={{ flex: 1, width: '100%' }}>
          {/* Desktop: fixed-height header with the divider pinned to the bottom
              (mt:auto) so it lines up with the calendar's divider at HEADER_H.
              Mobile: collapse — the calendar isn't shown alongside it. */}
          <Box
            sx={{
              height: embedded ? 'auto' : HEADER_H,
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              sx={{ mt: embedded ? 0 : 'auto', textAlign: 'center', pb: 1 }}
            >
              יש לבחור את השעה הנוחה לך:
            </Typography>
            <Box sx={{ width: '100%', borderBottom: `1px solid #1F2937` }} />
          </Box>
          {loadingSlots ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : slotsUnavailable ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ color: IL.muted }}>
                לא הצלחנו לטעון את הזמינות כרגע. ייתכן שיש תורים פנויים.
              </Typography>
              <Button
                size="small"
                onClick={() => setReloadKey((k) => k + 1)}
                sx={{ mt: 1 }}
              >
                נסו שוב
              </Button>
            </Box>
          ) : daySlots.length === 0 ? (
            <Typography sx={{ color: IL.muted, py: 2 }}>
              אין שעות פנויות ביום זה
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {daySlots.map((s) => {
                const selected = selectedValue === s.value;
                return (
                  <Box
                    key={s.value}
                    role="button"
                    tabIndex={0}
                    dir="ltr"
                    onClick={() => setSelectedValue(s.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        setSelectedValue(s.value);
                    }}
                    sx={{
                      textAlign: 'center',
                      py: 0.75,
                      borderRadius: 5,
                      cursor: 'pointer',
                      color: selected ? IL.white : '#1F2937',
                      bgcolor: selected ? '#17C68C' : 'transparent',
                      '&:hover': {
                        bgcolor: selected ? '#14B37E' : 'rgba(95,183,138,0.1)',
                      },
                    }}
                  >
                    {s.display}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        )}

        {/* Calendar column — mobile shows it only in the date step */}
        {(!embedded || step === 'date') && (
        <Box sx={{ flex: 1, width: '100%' }}>
          {/* Fixed-height header: month nav at top, weekday row (with divider)
              pinned to the bottom so its divider lines up with the slots one. */}
          <Box sx={{ height: HEADER_H, mb: 1, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.5,
              }}
            >
              {/* RTL: right arrow = previous month, left arrow = next */}
              <ArrowRightIcon
                onClick={() => goMonth(-1)}
                sx={{ cursor: 'pointer', color: IL.dark }}
              />
              <Typography
                sx={{ fontSize: { xs: 20, md: 28 }, minWidth: 120, textAlign: 'center' }}
              >
                {cursor ? (
                  `${HEBREW_MONTHS[cursor.m]} ${cursor.y}`
                ) : (
                  <Skeleton width={110} sx={{ mx: 'auto' }} />
                )}
              </Typography>
              <ArrowLeftIcon
                onClick={() => goMonth(1)}
                sx={{ cursor: 'pointer', color: IL.dark }}
              />
            </Box>

            {/* Sunday (א) on the right, Saturday (ש) on the left — reversed in data. */}
            <Box dir="ltr" sx={{ mt: 'auto' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  textAlign: 'center',
                  borderBottom: `1px solid #1F2937`,
                  pb: 1,
                }}
              >
                {[...HEBREW_WEEKDAYS].reverse().map((w) => (
                  <Typography
                    key={w}
                    sx={{
                      color:
                        w === 'ש' && !embedded ? IL.saturday : IL.dark,
                    }}
                  >
                    {w}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Sunday (א) on the right, Saturday (ש) on the left — reversed in data. */}
          <Box dir="ltr">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                rowGap: 1,
                textAlign: 'center',
              }}
            >
              {!cursor || loadingSlots
                ? Array.from({ length: 35 }).map((_, i) => (
                    <Skeleton
                      key={`sk-${i}`}
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{ mx: 'auto' }}
                    />
                  ))
                : calendarCells.map((day, idx) => {
                if (day == null) return <Box key={`b-${idx}`} />;
                const dateObj = new Date(cursor!.y, cursor!.m, day);
                const dateStr = ymd(dateObj);
                const isSat = dateObj.getDay() === 6;
                const isAvail = availableDates.has(dateStr);
                const isSelected = selectedDate === dateStr;

                return (
                  <Box
                    key={dateStr}
                    onClick={() => {
                      if (isAvail) {
                        setSelectedDate(dateStr);
                        setSelectedValue(null);
                      }
                    }}
                    sx={{
                      mx: 'auto',
                      width: 40,
                      height: 40,
                      lineHeight: '40px',
                      borderRadius: '50%',
                      cursor: isAvail ? 'pointer' : 'default',
                      // fontWeight: isAvail ? 700 : 400,
                      color: isSelected
                        ? IL.white
                        : isAvail
                        ? '#1F2937'
                        : isSat
                        ? embedded
                          ? '#1F2937'
                          : IL.saturday
                        : '#C9DAF9',
                      bgcolor: isSelected ? '#17C68C' : 'transparent',
                      '&:hover': isAvail
                        ? { bgcolor: isSelected ? '#14B37E' : 'rgba(95,183,138,0.12)' }
                        : undefined,
                    }}
                  >
                    {day}
                  </Box>
                );
                  })}
            </Box>
          </Box>
        </Box>
        )}
      </Box>
      </Box>

      {/* Mobile date step: advance to the time step. Otherwise: submit. */}
      {embedded && step === 'date' ? (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            onClick={() => setStep('time')}
            disabled={!selectedDate}
            sx={actionBtnSx}
          >
            בחר תאריך
          </Button>
          <Box
            sx={{
              width: 36,
              height: 3,
              bgcolor: IL.blueGrey,
              borderRadius: 2,
              mx: 'auto',
              mt: 2,
            }}
          />
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', mt: embedded ? 2 : 4 }}>
          {/* Waiting-list opt-in, shown before confirming a book/reschedule */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: embedded ? 13 : 15, color: '#1F2937' }}>
              מעוניינים להצטרף לרשימת המתנה?
            </Typography>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={waitingList}
                  onChange={(e) => setWaitingList(e.target.checked)}
                  size="small"
                  sx={{
                    p: 0.5,
                    color: '#1F2937',
                    '&.Mui-checked': { color: '#17C68C' },
                  }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: embedded ? 12 : 14, color: '#1F2937' }}
                >
                  צרו איתי קשר במידה ויתפנה תור במועד קרוב יותר
                </Typography>
              }
            />
          </Box>
          <Button
            onClick={submit}
            disabled={!selectedValue || submitting}
            sx={actionBtnSx}
          >
            {submitting ? (
              <CircularProgress size={22} sx={{ color: IL.muted }} />
            ) : mode === 'book' ? (
              'קבע פגישה'
            ) : (
              'עדכן מועד'
            )}
          </Button>
          {embedded && (
            <Box
              sx={{
                width: 36,
                height: 3,
                bgcolor: IL.blueGrey,
                borderRadius: 2,
                mx: 'auto',
                mt: 2,
              }}
            />
          )}
        </Box>
      )}
    </>
  );
}

export default SchedulingPanel;
